import { Component, computed, effect, inject, untracked, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { BreadcrumbsEnum, BreadcrumbsMenuContextEnum } from '../../../shared/models/breadcrumbs.enum';
import { BreadcrumbComponent as XngBreadcrumbComponent, BreadcrumbItemDirective } from 'xng-breadcrumb';

import { BreadcrumbFirstPartPipe } from '../../../shared/pipes/breadcrumb-first-part.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { BreadcrumbDoesContainSecondPartPipe } from '../../../shared/pipes/breadcrumb-does-contain-second-part.pipe';
import { BreadcrumbSecondPartPipe } from '../../../shared/pipes/breadcrumb-second-part.pipe';
import { ButtonComponent } from '../../../../components/kit/button/button.component';
import { featureOn, Features } from '../../../../feature-flag';
import { Menu } from '@openng/optimus-ui/menu';
import { MenuItem } from '@openng/optimus-ui/api';
import { BreadcrumbStore } from './breadcrumb.store';
import { CheckInputComponent } from '../../../../components/kit/input/check-input/check-input.component';
import { SubPage } from './breadcrumb.types';
import { ActivatedRoute } from '@angular/router';
import {
    UserTasksDialogComponent,
} from '../../../../components/social-service/user/user-tasks/user-tasks-dialog/user-tasks-dialog.component';
import { BaseDialogService } from '../../../../components/kit/base-dialog/base-dialog-service';
import {
    IndividualPlanDialogComponent,
} from '../../../../components/social-service/folder/individual-plan/individual-plan-dialog/individual-plan-dialog.component';
import {
    FolderServiceMailRecordsDialogComponent,
} from '../../../content/pages/person/folder-service-mail-records/folder-service-mail-records-dialog/folder-service-mail-records-dialog.component';
import {
    FolderDocumentsDialogComponent,
} from '../../../content/pages/person/folder-documents/folder-documents-dialog/folder-documents-dialog.component';
import {
    FolderContinuousRecordDialogComponent,
} from '../../../../components/social-service/folder/folder-continuous-record/folder-continuous-record-dialog/folder-continuous-record-dialog.component';
import {
    PersonContactsDialogComponent,
} from '../../../content/pages/person/person-contacts/person-contacts-dialog/person-contacts-dialog.component';
import {
    SERVICE_SETTINGS_BASIC_SOCIAL_CONSULTING_KEY,
    SERVICE_SETTINGS_CONTACTS_TYPE_KEY,
    SERVICE_SETTINGS_CONTINUOUS_RECORD_TYPE_KEY,
    SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY,
    SERVICE_SETTINGS_MAIL_TYPE_KEY,
    SERVICE_SETTINGS_TASKS_TYPE_KEY,
    SERVICE_SETTINGS_WAREHOUSE_MATERIAL_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import {
    BasicSocialConsultingUpsertDialogComponent,
} from '../../../../components/social-service/basic-social-consulting/basic-social-consulting-upsert-dialog/basic-social-consulting-upsert-dialog.component';
import {
    WarehouseMaterialOperationDialogListComponent,
} from 'packages/client/src/app/components/social-service/folder/warehouse-material/warehouse-material-operation-dialog-list/warehouse-material-operation-dialog-list.component';
import {
    WorkSuggestionDialogComponent,
} from '../../../../components/social-service/work/pages/work-suggestion-page/work-suggestion-dialog/work-suggestion-dialog.component';
import { WorkDataSuggestionTypeEnum } from '@libs/types/src/services/enum/work-data-suggestion-type.enum';
import {
    WaitingListDialogComponent,
} from 'packages/client/src/app/components/social-service/waiting-list/waiting-list-dialog/waiting-list-dialog.component';
import {
    WorkMassWorkDialogComponent,
} from '../../../../components/social-service/work/work-mass-work/work-mass-work-dialog/work-mass-work-dialog.component';
import {
    WorkGroupActivityDialogComponent,
} from '../../../../components/social-service/work/work-group-activity/work-group-activity-dialog/work-group-activity-dialog.component';
import {
    WorkContinuousRecordDialogComponent,
} from '../../../../components/social-service/work/work-continuous-record/work-continuous-record-dialog/work-continuous-record-dialog.component';
import {
    WorkOperationalWorkDialogComponent,
} from '../../../../components/social-service/work/work-operational-work/work-operational-work-dialog/work-operational-work-dialog.component';

@Component({
    selector: 'app-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    providers: [
        BreadcrumbStore,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        XngBreadcrumbComponent,
        BreadcrumbItemDirective,
        BreadcrumbFirstPartPipe,
        TranslatePipe,
        BreadcrumbDoesContainSecondPartPipe,
        BreadcrumbSecondPartPipe,
        ButtonComponent,
        Menu,
        CheckInputComponent,
    ],
})
export class BreadcrumbComponent {

    protected readonly store = inject(BreadcrumbStore);
    protected readonly route = inject(ActivatedRoute);
    protected readonly dialogService = inject(BaseDialogService);

    protected readonly checkboxes = viewChildren<CheckInputComponent>('subPageCheck');

    protected readonly subPageMenuItems = computed<(MenuItem & SubPage)[]>(() => {
        const pages = <(MenuItem & SubPage)[]>[
            {
                label: 'breadcrumbs.contacts',
                code: 'contacts',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}+${BreadcrumbsMenuContextEnum.FOLDER}`,
                serviceSettingsKey: SERVICE_SETTINGS_CONTACTS_TYPE_KEY,
                isAvailableCallback: () => this.store.isAllowed('folderContact.read')(),
                component: PersonContactsDialogComponent,
            },
            {
                label: 'breadcrumbs.individualPlans',
                code: 'individualPlan',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}+${BreadcrumbsMenuContextEnum.FOLDER}`,
                serviceSettingsKey: SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY,
                component: IndividualPlanDialogComponent,
            },
            {
                label: 'breadcrumbs.workContinuousRecords',
                code: 'continuousRecords',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}+${BreadcrumbsMenuContextEnum.FOLDER}`,
                serviceSettingsKey: SERVICE_SETTINGS_CONTINUOUS_RECORD_TYPE_KEY,
                isAvailableCallback: () => this.store.isAllowed('continuousReport.read')(),
                component: FolderContinuousRecordDialogComponent,
            },
            {
                label: 'breadcrumbs.tasks',
                code: 'socialTask',
                context: BreadcrumbsMenuContextEnum.SOCIAL_SERVICE,
                serviceSettingsKey: SERVICE_SETTINGS_TASKS_TYPE_KEY,
                isAvailableCallback: () => this.store.isAllowed('task.read')(),
                component: UserTasksDialogComponent,
            },
            {
                label: 'breadcrumbs.documents',
                code: 'documents',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}+${BreadcrumbsMenuContextEnum.FOLDER}`,
                component: FolderDocumentsDialogComponent,
            },
            {
                label: 'breadcrumbs.warehouseMaterial',
                code: 'warehouseMaterial',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                serviceSettingsKey: SERVICE_SETTINGS_WAREHOUSE_MATERIAL_TYPE_KEY,
                component: WarehouseMaterialOperationDialogListComponent,
                featureFlag: Features.WAREHOUSE_MATERIAL,
            },
            {
                label: 'breadcrumbs.mail',
                code: 'mail',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}+${BreadcrumbsMenuContextEnum.FOLDER}`,
                serviceSettingsKey: SERVICE_SETTINGS_MAIL_TYPE_KEY,
                component: FolderServiceMailRecordsDialogComponent,
                featureFlag: Features.SOCIAL_JANUARY,
                isAvailableCallback: () => this.store.isAllowed('folderMail.read')(),
            },
            {
                label: 'breadcrumbs.basicSocialConsulting',
                code: 'basicSocialConsulting',
                context: BreadcrumbsMenuContextEnum.SOCIAL_SERVICE,
                serviceSettingsKey: SERVICE_SETTINGS_BASIC_SOCIAL_CONSULTING_KEY,
                component: BasicSocialConsultingUpsertDialogComponent,
            },
            {
                label: 'breadcrumbs.waitingList',
                code: 'waitingList',
                context: BreadcrumbsMenuContextEnum.SOCIAL_SERVICE,
                isAvailableCallback: () => {
                    return this.store.isAllowed('waitingList.read')();
                },
                component: WaitingListDialogComponent,
            },
            {
                label: 'breadcrumbs.workSuggestionSatisfaction',
                code: 'workSuggestionSatisfaction',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                component: WorkSuggestionDialogComponent,
                featureFlag: Features.SUGGESTION,
                isAvailableCallback: () => this.store.isAllowed('workSuggestion.create')(),
                dialogConfig: { data: { suggestionType: WorkDataSuggestionTypeEnum.satisfaction } },
            },
            {
                label: 'breadcrumbs.workSuggestionComplaint',
                code: 'workSuggestionComplaint',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                component: WorkSuggestionDialogComponent,
                featureFlag: Features.SUGGESTION,
                isAvailableCallback: () => this.store.isAllowed('workSuggestion.create')(),
                dialogConfig: { data: { suggestionType: WorkDataSuggestionTypeEnum.complaint } },
            },
            {
                label: 'breadcrumbs.workSuggestionFinding',
                code: 'workSuggestionFinding',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                component: WorkSuggestionDialogComponent,
                featureFlag: Features.SUGGESTION,
                isAvailableCallback: () => this.store.isAllowed('workSuggestion.create')(),
                dialogConfig: { data: { suggestionType: WorkDataSuggestionTypeEnum.finding } },
            },
            {
                label: 'breadcrumbs.workContinuousRecord',
                code: 'workContinuousRecord',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                isAvailableCallback: () => this.store.isAllowed('continuousReport.create')(),
                component: WorkContinuousRecordDialogComponent,
            },
            {
                label: 'breadcrumbs.workGroupActivity',
                code: 'workGroupActivity',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                isAvailableCallback: () => this.store.isAllowed('workGroupActivity.create')(),
                component: WorkGroupActivityDialogComponent,
            },
            {
                label: 'breadcrumbs.workMassWork',
                code: 'workMassWork',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                isAvailableCallback: () => this.store.isAllowed('workMassWork.create')(),
                component: WorkMassWorkDialogComponent,
            },
            {
                label: 'breadcrumbs.workOperationalWork',
                code: 'workOperationalWork',
                context: `${BreadcrumbsMenuContextEnum.SOCIAL_SERVICE}`,
                featureFlag: Features.NEW_CALENDAR,
                isAvailableCallback: () => this.store.isAllowed('operationalWork.create')(),
                component: WorkOperationalWorkDialogComponent,
            },
        ];

        const contexts = this.store.breadcrumbMenuContexts();

        return pages
            .filter((page) => {
                if (page.featureFlag && !featureOn[page.featureFlag]) return false;
                if (page.isAvailableCallback && !page.isAvailableCallback()) return false;
                if (page.context === undefined) return true;

                const checks: string[] = Array.isArray(page.context) ? page.context : [page.context];

                return checks.every((condition) => {
                    const list = condition.split('+');
                    return list.every((it) => contexts.has(it));
                });
            })
            .filter((page) => {
                return this.store.globalGlobalStore.organizationStore.isServiceSettingsAvailable(page.serviceSettingsKey, this.store.globalGlobalStore.loggedUserStore.selectedServiceId());
            })
            ;
    });

    public readonly visiblePages = computed(() => {
        const available = this.subPageMenuItems() || [];
        const active = this.store.currentMenuOptions();

        return available.filter((page) =>
            active.includes(page.code)
            && (!page.featureFlag || featureOn[page.featureFlag]),
        );
    });

    constructor() {
        effect(() => {
            const activated = untracked(() => this.store.currentMenuOptions());
            const checks = this.checkboxes();
            if (!checks.length || !activated) return;

            checks.forEach((it) => {
                it.setSelected(activated.includes(it.id()));
            });
        });
    }

    protected toggleItem(event: Event, item: MenuItem): void {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();

        const url = this.store.currentRawUrl();
        if (!url) return;

        let activated = this.store.currentMenuOptions();
        const code: string = item.code;
        if (!code) return;

        const check = this.checkboxes().find((it) => it.id() == code);
        if (activated.includes(code)) {
            activated = activated.filter((it) => it != code);
            check?.setSelected(false);
        } else {
            activated = [...activated, code];
            check?.setSelected(true);
        }

        this.store.api.upsertBreadcrumbMenu({
            url,
            options: activated,
        }, 'url');

        this.store.breadcrumbMenus().update((_arr) => {
            const arr = [..._arr];
            const menu = arr.find((it) => it.url == url);
            if (menu) {
                menu.options = activated;
            } else {
                arr.push({ url, options: activated } as any);
            }

            return arr;
        });
    }

    protected openPage(page: MenuItem & SubPage, event: Event) {
        this.dialogService.openSubPageDialog(page, page.dialogConfig);
    }

    protected readonly BreadcrumbsEnum = BreadcrumbsEnum;
    protected readonly featureOn = featureOn;

}
